import React, { useState } from 'react'
import { Loader2, Plus, Search } from 'lucide-react'
import Markdown from './Markdown'
import { useDiscoverChannels, useAddChannel } from '../hooks/useTranscripts'
import { useAddFeed } from '../hooks/useFeeds'

// F3.1 channel-discovery results (shared by Sources → Add channel and
// Research → Discover): AI-consolidated shortlist + per-source candidate rows.
// YouTube/Rutube rows register the channel; podcast rows subscribe the feed.

const fmtK = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n))

export function ChannelResults({ data, onAdded }) {
  const add = useAddChannel()
  const addFeed = useAddFeed()
  const [done, setDone] = useState({}) // key -> 'added' | 'subscribed'
  if (!data) return null

  const addChannel = (src, c, key) =>
    c.channel_id &&
    add.mutate(
      { source: src, channel_id: String(c.channel_id) },
      { onSuccess: (r) => { setDone((p) => ({ ...p, [key]: 'added' })); if (r?.channel_id) onAdded?.(r.channel_id) } }
    )
  const subscribe = (c, key) =>
    c.feed_url &&
    addFeed.mutate({ url: c.feed_url }, { onSuccess: () => setDone((p) => ({ ...p, [key]: 'subscribed' })) })

  return (
    <>
      {data.ai_summary && (
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-3 text-sm dark:border-gold/30">
          <div className="text-[11px] uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-1">AI shortlist</div>
          <Markdown>{data.ai_summary}</Markdown>
        </div>
      )}
      {['youtube', 'rutube', 'podcast'].map((src) => {
        const list = data.results?.[src] ?? []
        if (!list.length) return null
        return (
          <div key={src} className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50">{src}</div>
            {list.map((c, i) => {
              const key = `${src}:${c.channel_id ?? c.id ?? i}`
              return (
                <div key={key} className="flex items-center gap-3 rounded-lg border border-parchment-border bg-white/50 px-3 py-2 dark:border-midnight-600 dark:bg-midnight-800/40">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink dark:text-mist truncate">
                      {c.url ? (
                        <a href={c.url} target="_blank" rel="noreferrer" className="hover:underline">{c.title}</a>
                      ) : c.title}
                    </div>
                    <div className="text-xs text-ink-soft dark:text-mist/50 truncate">
                      {c.followers != null && <>👥 {fmtK(c.followers)} · </>}
                      {c.views_sum != null && c.followers == null && <>👁 {fmtK(c.views_sum)} · </>}
                      {c.videos_matched != null && <>{c.videos_matched} matched · </>}
                      {c.episodes != null && <>{c.episodes} episodes · </>}
                      {(c.sample_titles ?? []).slice(0, 2).join(' · ') || c.channel || c.description || ''}
                    </div>
                  </div>
                  {done[key] ? (
                    <span className="shrink-0 text-xs text-emerald-700 dark:text-emerald-300">✓ {done[key]}</span>
                  ) : src === 'podcast' ? (
                    <button
                      onClick={() => subscribe(c, key)}
                      disabled={!c.feed_url || addFeed.isPending}
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
                    >
                      <Plus className="w-3.5 h-3.5" /> Subscribe feed
                    </button>
                  ) : (
                    <button
                      onClick={() => addChannel(src, c, key)}
                      disabled={!c.channel_id || add.isPending}
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add channel
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </>
  )
}

// Input + results, used inside Sources → Add channel ("find channels by topic").
export default function ChannelDiscovery({ onAdded }) {
  const discover = useDiscoverChannels()
  const [q, setQ] = useState('')
  const run = () => q.trim() && discover.mutate({ query: q.trim(), n: 5 })

  return (
    <div className="pt-2 border-t border-parchment-border dark:border-midnight-600 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-soft dark:text-mist/50">…or find channels by topic:</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="e.g. MCP model context protocol tutorials"
          className="h-9 flex-1 min-w-[240px] rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />
        <button
          onClick={run}
          disabled={discover.isPending || !q.trim()}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {discover.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Find channels
        </button>
      </div>
      {discover.isPending && (
        <p className="text-xs text-ink-soft dark:text-mist/50">
          Searching YouTube, Rutube and podcast directories, weighting by popularity…
        </p>
      )}
      {discover.isError && <p className="text-xs text-red-600">{discover.error?.message}</p>}
      <ChannelResults data={discover.data} onAdded={onAdded} />
    </div>
  )
}
